import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce, isString, isUndefined, startsWith } from 'lodash';
import { NameSelectWithTabs } from '@src/emp-3.0/components/custom-dimension/component/NamedSelectWithExtras/index';
import {
  EVENT_KEYS_PREFIX,
  EVENT_TAG_TAB_KEY,
  GLOBAL_KEYS_PREFIX,
  GLOBAL_TAG_TAB_KEY,
} from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/constant';
import {
  checkKeyType,
  generateTagKeyTabItems,
  suffixIcon,
} from '../../custom-dimension-panel/components/RuleBuilder';
import {
  addTextPrefix,
  isGlobalAttributeType,
  transformAttributeType,
  trimStartPrefix,
} from '@src/emp-3.0/components/custom-dimension/utils';
import { useFetchAttributeTypes } from '@src/emp-3.0/api/queryHooks';
import { EAttributeType, TAttributeType } from '@src/emp-3.0/types';

import { AttributeSelectWrapper } from './styled';
import { generateFilter } from '@hooks/useHandleFilter';
import { mappedEventDimensionType } from '@src/common/constant/enum';
import { isNull } from '@src/emp-3.0/utils/checks';

interface IAttributeSelect {
  disabled?: boolean;
  eventId?: number | string;
  eventName?: string;
  interval?: any;
  value?: string;
  onChange?: (value: string | undefined) => void;
  attributeType?: TAttributeType; // 1 event 2 global
  onAttributeTypeChange?: (attributeType: TAttributeType | undefined) => void;
}

const AttributeSelect: React.FC<IAttributeSelect> = ({
  disabled = false,
  eventId,
  eventName,
  value = '',
  onChange,
  attributeType: propsAttributeType = EAttributeType.Global,
  onAttributeTypeChange,
}) => {
  const [eventTag, setEventTag] = useState<string>(
    !value ? value : addTextPrefix(value, transformAttributeType(propsAttributeType)),
  );
  const [attributeType, setAttributeType] = useState<TAttributeType>(propsAttributeType);

  const isGlobalAttribute = useMemo(
    () => (isNull(attributeType) ? undefined : isGlobalAttributeType(attributeType)),
    [attributeType],
  );

  const [eventTagTabsKey, setEventTagTabsKey] = useState<string>(
    isGlobalAttribute || isNull(attributeType) ? GLOBAL_TAG_TAB_KEY : EVENT_TAG_TAB_KEY,
  );
  useEffect(() => {
    setEventTagTabsKey(
      isGlobalAttribute || isNull(attributeType) ? GLOBAL_TAG_TAB_KEY : EVENT_TAG_TAB_KEY,
    );
  }, [attributeType, isGlobalAttribute]);
  const [searchText, setSearchText] = useState<string>('');
  const refLastEventName = useRef<string>();

  const filters = useMemo(() => {
    if (typeof eventId === 'string') {
      return [[generateFilter(mappedEventDimensionType.all_event_name, eventId, 0)]];
    }
    return;
  }, [eventId]);

  const { data: attributeTypesData, isLoading: isGetAttributeTypesLoading } =
    useFetchAttributeTypes({ value: searchText, eventName, filters });

  const { globalTagsOptions, eventTagsOptions } = useMemo(() => {
    const { global_keys = [], event_keys = [] } = attributeTypesData || {};
    const globalTagsOptions = global_keys.map((tag) => ({
      label: tag,
      value: addTextPrefix(tag, GLOBAL_KEYS_PREFIX),
    }));
    const eventTagsOptions = event_keys.map((tag) => ({
      label: tag,
      value: addTextPrefix(tag, EVENT_KEYS_PREFIX),
    }));
    return {
      globalTagsOptions,
      eventTagsOptions,
    };
  }, [attributeTypesData]);

  // check whether value is in the attribute list of current event once event is changed
  useEffect(() => {
    if (!isString(eventName)) {
      return;
    }
    if (!refLastEventName.current) {
      refLastEventName.current = eventName;
      return;
    }
    if (refLastEventName.current === eventName || !attributeTypesData) {
      return;
    }
    refLastEventName.current = eventName;
    if (!value) {
      return;
    }
    const allTagKeys = Object.values(attributeTypesData).flat();
    if (!allTagKeys.includes(value)) {
      onChange?.(undefined);
      onAttributeTypeChange?.(undefined);
    }
  }, [eventName, attributeTypesData, value, onChange, onAttributeTypeChange]);

  // Prevention: When the metric value is not included in the attribute_types API, the UI displays the prefixed (__event__/__global__)value.
  const attributeSelectValue = useMemo(() => {
    if (!value) return eventTag;

    const options = eventTagTabsKey === GLOBAL_TAG_TAB_KEY ? globalTagsOptions : eventTagsOptions;

    const findValue = options.find((item) => item.value === eventTag);
    return findValue ? eventTag : value;
  }, [eventTag, eventTagTabsKey, eventTagsOptions, globalTagsOptions, value]);

  const tagsOptions = useMemo(() => {
    return eventTagTabsKey === GLOBAL_TAG_TAB_KEY ? globalTagsOptions : eventTagsOptions;
  }, [eventTagTabsKey, eventTagsOptions, globalTagsOptions]);

  const globalTagKeyCount = useMemo(() => globalTagsOptions.length, [globalTagsOptions]);

  const eventTagKeyCount = useMemo(() => eventTagsOptions.length, [eventTagsOptions]);

  useEffect(() => {
    if (value) {
      setEventTag(addTextPrefix(value, transformAttributeType(propsAttributeType)));
    } else {
      setEventTag(value);
    }
  }, [propsAttributeType, value]);

  useEffect(() => {
    setAttributeType(propsAttributeType);
  }, [propsAttributeType]);

  const handleAttributeTypeChange = (value: string) => {
    const type = checkKeyType({ value, attributeTypesData, eventTagTabsKey });
    setAttributeType(type);
    onAttributeTypeChange?.(type);
  };

  const handleEventTagChange = (value: string) => {
    setSearchText('');
    const prefix = startsWith(value, EVENT_KEYS_PREFIX) ? EVENT_KEYS_PREFIX : GLOBAL_KEYS_PREFIX;
    const actualEventTag = trimStartPrefix(value, prefix);
    setEventTag(value);
    onChange?.(actualEventTag);
    handleAttributeTypeChange(actualEventTag);
  };

  return (
    <AttributeSelectWrapper suffixIconWidth={isGlobalAttribute || isNull(attributeType) ? 60 : 100}>
      <NameSelectWithTabs
        activeTabKey={eventTagTabsKey}
        items={generateTagKeyTabItems({
          globalTagKeyCount,
          eventTagKeyCount,
        })}
        onTabsItemClick={(value) => setEventTagTabsKey(value.key)}
        selectProps={{
          loading: isGetAttributeTypesLoading,
          disabled,
          options: tagsOptions,
          showSearch: true,
          filterOption: false,
          value: attributeSelectValue || undefined,
          onChange: handleEventTagChange,
          onSearch: debounce((value) => setSearchText(value), 1000),
          suffixIcon: suffixIcon(isGetAttributeTypesLoading, isGlobalAttribute),
          style: { maxWidth: 400, width: '100%' },
        }}
      />
    </AttributeSelectWrapper>
  );
};

export default AttributeSelect;
